import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum, getCrudStatusLabel, permissionLevelEnum } from '../../../../constants';
import { getCrudStatusColor } from '../../../../constants/colors';
import { useUpdateFormTypeMutation } from '../../../../hooks/mutations/useFormTypeMutation';
import { useFormTypesQuery } from '../../../../hooks/queries/useFormTypesQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { FormType } from '../../../../services/apiSLTP/qhse/forms/formTypes';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { createDateTimeColumn } from '../../../../utils/tables/commonColumns';
import StateCell from '../../../cells/StateCell';
import UpdateFormTypeModal from '../../../modals/admin/configuration/UpdateFormTypeModal';
import DeleteModal from '../../../modals/DeleteModal';
import IconButton from '../../../ui/IconButton';
import IconLine from '../../../ui/IconLine';
import GenericAgGridTable from '../../GenericAgGridTable';

const FormTypesSettingsRow: React.FC = () => {
    const { t } = useTranslation();
    const { user } = useAuth();

    const [selectedType, setSelectedType] = useState<FormType | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);

    const { data: dataTypes, isLoading, refetch } = useFormTypesQuery({});

    const { mutate } = useUpdateFormTypeMutation({
        onSuccess: () => refetch(),
    });

    const handleEdit = (type: FormType) => {
        setSelectedType(type);
        setIsEditMode(true);
        setShowEditModal(true);
    };

    const handleDelete = (type: FormType) => {
        setSelectedType(type);
        setShowDeleteModal(true);
    };

    const columnDefs: ColDef<FormType>[] = [
        {
            cellStyle: (params) => {
                const marginTop = '5px';
                const marginBottom = '5px';

                const height = `calc(100% - ${parseInt(marginTop) + parseInt(marginBottom)}px)`;

                return {
                    padding: 0,
                    marginLeft: 5,
                    backgroundColor: params.data?.color || 'transparent',
                    marginTop,
                    marginBottom,
                    height,
                    borderBottomLeftRadius: 10,
                    borderBottomRightRadius: 10,
                    borderTopLeftRadius: 10,
                    borderTopRightRadius: 10,
                    width: 20,
                };
            },
            colId: 'color',
            resizable: false,
            filter: false,
            sortable: false,
            minWidth: 30,
            width: 30,
            valueGetter: (params) => params.data?.color || '',
            cellRenderer: () => '',
            enableRowGroup: false,
            suppressHeaderMenuButton: true,
        },
        {
            headerName: capitalizeFirstLetter(t('designation')),
            field: 'designation',
            colId: 'designation',
        },
        {
            headerName: capitalizeFirstLetter(t('description')),
            field: 'description',
            initialFlex: 1,
            minWidth: 250,
            colId: 'description',
            cellStyle: { whiteSpace: 'pre-wrap' },
        },
        {
            headerName: capitalizeFirstLetter(t('actions')),
            cellRenderer: (params: ICellRendererParams<FormType>) => {
                if (!params.data) return null;
                return (
                    <IconLine>
                        <IconButton
                            iconName="edit"
                            title={capitalizeFirstLetter(t('modifyFormType'))}
                            onClick={() => handleEdit(params.data!)}
                            disabled={params.data.crudStatus === crudStatusEnum.DELETED}
                        />
                        {user && user.profileId.permsLevel <= permissionLevelEnum.ADMIN && (
                            <IconButton
                                iconName="delete"
                                title={capitalizeFirstLetter(t('deleteFormType'))}
                                onClick={() => handleDelete(params.data!)}
                                disabled={params.data.crudStatus === crudStatusEnum.DELETED}
                            />
                        )}
                    </IconLine>
                );
            },
            colId: 'actions',
            filter: false,
            sortable: false,
        },
        {
            headerName: capitalizeFirstLetter(t('status')),
            valueGetter: (params) => capitalizeFirstLetter(t(getCrudStatusLabel(params.data?.crudStatus))),
            cellRenderer: (params: ICellRendererParams<FormType>) => {
                if (!params.data) return params.value;
                return <StateCell stateColor={getCrudStatusColor(params.data?.crudStatus)} text={t(getCrudStatusLabel(params.data?.crudStatus))} />;
            },
            colId: 'status',
        },

        createDateTimeColumn<FormType>('createdAt', 'createdAt'),
        createDateTimeColumn<FormType>('updatedAt', 'updatedAt'),
    ];

    if (user?.profileId.permsLevel === permissionLevelEnum.ADMIN) {
        columnDefs.push({
            headerName: capitalizeFirstLetter(t('_id')),
            field: '_id',
            initialWidth: 250,
            cellClass: 'usage-col-main',
            headerClass: 'usage-col-main',
            colId: '_id',
        });
    }

    return (
        <>
            <GenericAgGridTable
                columnDefs={columnDefs}
                rowData={dataTypes?.sort((a, b) => a.designation.localeCompare(b.designation)) || []}
                loading={isLoading}
                headerRightComponent={
                    <IconButton
                        iconName="addCircle"
                        onClick={() => {
                            setSelectedType(null);
                            setIsEditMode(false);
                            setShowEditModal(true);
                        }}
                        title={capitalizeFirstLetter(t('addFormType'))}
                    />
                }
                exportCsvFileName="admin-form-types"
                agGridProps={{ getRowId: ({ data }) => data._id }}
            />

            {showEditModal && (
                <UpdateFormTypeModal
                    showEditModal={showEditModal}
                    setShowEditModal={setShowEditModal}
                    formType={selectedType}
                    isEditMode={isEditMode}
                    refetch={refetch}
                />
            )}

            {showDeleteModal && selectedType && (
                <DeleteModal
                    open={showDeleteModal}
                    handleClose={() => setShowDeleteModal(false)}
                    deleteAction={() => mutate({ formTypeId: selectedType._id, data: { crudStatus: crudStatusEnum.DELETED } })}
                    title={t('formType')}
                    itemName={selectedType.designation}
                />
            )}
        </>
    );
};

export default FormTypesSettingsRow;
